import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/api';

// Helper to extract only essential user fields for storage
const extractUserFields = (user) => {
  if (!user) return null;
  return {
    id: user.id, // 保留 id 字段，用于权限判断
    username: user.username,
    email: user.email,
    auth_provider: user.auth_provider,
    last_login: user.last_login,
    last_login_ip: user.last_login_ip,
  };
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      // 保存最后一次登录的凭据（不包含密码）
      lastLoginCredentials: {
        email: '',
        rememberEmail: false
      },

      setAuthState: (state) => {
        set(state);
      },

      login: async (email, password, rememberEmail = false) => {
        try {
          const response = await authService.login({ email, password });
          const { user, token } = response.data;
          
          // 保存认证信息
          set({ 
            user, 
            token, 
            isAuthenticated: true,
            lastLoginCredentials: {
              email: rememberEmail ? email : '',
              rememberEmail
            }
          });
          
          return { success: true };
        } catch (error) {
          return { 
            success: false, 
            message: error.response?.data?.message || 'Login failed, please check your email and password'
          };
        }
      },

      register: async (username, email, password) => {
        try {
          const response = await authService.register({
            username,
            email,
            password,
          });
          
          // 检查是否返回了验证消息
          if (response.data.message && (response.data.message.includes('验证') || response.data.message.includes('verification'))) {
            return { 
              success: true, 
              data: response.data,
              message: response.data.message,
              requiresVerification: true
            };
          }
          
          return { success: true, data: response.data };
        } catch (error) {
          console.error('注册错误详情:', error);
          console.error('错误响应:', error.response);
          return { 
            success: false, 
            message: error.response?.data?.message || 'Registration failed, please try again later' 
          };
        }
      },

      logout: () => {
        const { lastLoginCredentials } = get();
        console.log('执行退出登录，清除认证数据');
        
        // 清除认证数据，但保留登录凭据
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          // 保留最后一次登录的email
          lastLoginCredentials
        });
        
        // 清除其他可能的缓存数据
        // 如果需要完全清除localStorage中的其他数据，可以在这里添加
      },

      // 完全清除所有数据（包括记住的登录信息）
      clearAllData: () => {
        console.log('清除所有数据');
        set({ 
          user: null, 
          token: null, 
          isAuthenticated: false,
          lastLoginCredentials: {
            email: '',
            rememberEmail: false
          }
        });
      },

      checkAuth: async () => {
        const { token } = get();
        if (!token) return false;

        try {
          const response = await authService.getProfile();
          set({ user: response.data, isAuthenticated: true });
          return true;
        } catch (error) {
          console.log('认证检查失败，清除认证数据');
          set({ user: null, token: null, isAuthenticated: false });
          return false;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: extractUserFields(state.user),
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        lastLoginCredentials: state.lastLoginCredentials,
      }),
    }
  )
);
